import { createSlice } from '@reduxjs/toolkit';
import axios from '../../services/authService';
import plainAxios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { parseApiResponse } from '../../utilities/parseApiResponse';

// Initial state
const initialState = {
  loading: false,
  response: {},
  error: null,
};

// Async thunk for saving regression test results
// Two-step process: 1) Get presigned URL, 2) Upload to S3
export const saveRegressionResultsAsync = (payload) => async (dispatch) => {
  dispatch(reset());
  dispatch(setLoading(true));

  try {
    // Generate unique filename using regression_test_id and timestamp
    const timestamp = Date.now();
    const fileName = `${payload.regression_test_id}_${timestamp}.json`;

    // STEP 1: Get presigned URL from API
    const uploadRequestPayload = {
      fileName: fileName,
      fileType: 'application/json',
    };

    console.log('Step 1: Requesting presigned URL...', uploadRequestPayload);

    const presignedUrlResponse = await axios.post(
      API_ENDPOINTS.UPLOAD_REGRESSION_FILE,
      uploadRequestPayload,
    );

    console.log('Presigned URL response:', presignedUrlResponse.data);

    // Extract presigned URL from response
    // Handle different response structures (direct field or nested body)
    let presignedUrl = null;

    // Check direct response fields first (most common format)
    if (presignedUrlResponse.data?.uploadURL) {
      presignedUrl = presignedUrlResponse.data.uploadURL;
    } else if (presignedUrlResponse.data?.url) {
      presignedUrl = presignedUrlResponse.data.url;
    } else {
      const parsedBody = parseApiResponse(presignedUrlResponse);
      presignedUrl = parsedBody?.url || parsedBody?.uploadURL || parsedBody?.presignedUrl;
    }

    if (!presignedUrl) {
      throw new Error('No presigned URL returned from API');
    }

    console.log('Step 2: Uploading to S3...', presignedUrl);

    // STEP 2: Upload full payload to S3 using presigned URL (no auth header needed)
    const s3UploadResponse = await plainAxios.put(presignedUrl, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('S3 upload successful:', s3UploadResponse.status);

    // Prepare success response
    const successResponse = {
      fileName: fileName,
      uploadedAt: new Date(timestamp).toISOString(),
      s3Status: s3UploadResponse.status,
      s3StatusText: s3UploadResponse.statusText,
    };

    dispatch(saveRegressionResultsResponse(successResponse));
    return { success: true, data: successResponse, fileName: fileName };
  } catch (err) {
    console.error('Error saving regression results:', err);

    const errorMessage =
      err.response?.data?.message || err.message || 'Failed to save regression test results';

    dispatch(setError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Slice
const saveRegressionResultsSlice = createSlice({
  name: 'saveRegressionResults',
  initialState,
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.response = {};
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    saveRegressionResultsResponse: (state, action) => {
      state.loading = false;
      state.response = action.payload;
      state.error = null;
    },
    setError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

// Export actions
export const { reset, setLoading, saveRegressionResultsResponse, setError } =
  saveRegressionResultsSlice.actions;

// Export selectors
export const selectSaveRegressionResultsLoading = (state) => state.saveRegressionResults.loading;
export const selectSaveRegressionResultsResponse = (state) => state.saveRegressionResults.response;
export const selectSaveRegressionResultsError = (state) => state.saveRegressionResults.error;

// Export reducer
export default saveRegressionResultsSlice.reducer;
